import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtNL } from "@/lib/dates";
import { CalendarDays, Users, FolderKanban, Clock, Briefcase, ArrowUpRight } from "lucide-react";

const fmtEUR = (cents) =>
  cents == null
    ? "—"
    : new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format((cents || 0) / 100);

export default function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/dashboard/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="p-8 font-semibold">Laden…</div>;

  const officeAtLimit = s.office_used >= s.office_limit;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8">
      <h1 className="pb-heading text-3xl font-black mb-1">DASHBOARD</h1>
      <div className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-6">Week {fmtNL(s.week.from)} — {fmtNL(s.week.to)}</div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={CalendarDays} label="Bezettingsgraad" value={`${s.occupancy_percent}%`} sub={`${s.planned_manhours} / ${s.available_manhours} uur`}/>
        <Stat icon={Clock} label="Open gaten" value={`${s.open_gaps}u`} sub="Beschikbare uren zonder planning"/>
        <Stat icon={Users} label="Actieve uitvoerders" value={s.active_users}/>
        <Stat icon={FolderKanban} label="Actieve projecten" value={s.active_projects} sub={`${s.projects} totaal`}/>
      </div>

      {/* Billing summary — ingeplande gebruikers deze maand */}
      <div className="mt-6 pb-card p-5 border-l-8 border-orange-600" data-testid="dashboard-billing-summary">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="min-w-[220px]">
            <div className="text-xs font-black uppercase tracking-widest text-slate-500 flex items-center gap-2"><Users size={14}/> Ingeplande gebruikers deze maand</div>
            <div className="pb-heading text-4xl font-black mt-1 tabular-nums" data-testid="dashboard-planned-count">
              {s.planned_users_this_month ?? 0}
              <span className="text-slate-400 text-lg font-bold"> / {s.included_seats} inbegrepen</span>
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-700" data-testid="dashboard-billing-sentence">
              Deze maand {s.planned_users_this_month ?? 0} ingeplande gebruikers — je betaalt <span className="font-black">{fmtEUR(s.estimated_cost_cents)}</span>. Kantoor (eigenaar + planner) is gratis.
            </div>
          </div>
          <div className="min-w-[200px]">
            <div className="text-xs font-black uppercase tracking-widest text-slate-500 flex items-center gap-2"><Briefcase size={14}/> Kantooraccounts</div>
            <div className="pb-heading text-4xl font-black mt-1 tabular-nums" data-testid="dashboard-office-count">
              {s.office_used} <span className="text-slate-400 text-lg font-bold">/ {s.office_limit}</span>
            </div>
            <div className={`text-xs font-bold mt-1 ${officeAtLimit ? "text-red-700" : "text-slate-600"}`}>
              {officeAtLimit ? "Limiet bereikt — upgrade voor meer" : `${s.office_limit - s.office_used} plek(ken) beschikbaar`}
            </div>
            {officeAtLimit && (
              <Link to="/billing" className="mt-2 inline-flex items-center gap-1 text-orange-600 font-black text-xs uppercase tracking-widest" data-testid="dashboard-office-upgrade">
                Upgrade pakket <ArrowUpRight size={12}/>
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 pb-card p-6">
        <div className="pb-heading text-xl font-black mb-3">Bezetting deze week</div>
        <div className="w-full bg-slate-200 h-6 border-2 border-slate-900 rounded">
          <div className="h-full bg-orange-600" style={{ width: `${Math.min(100, s.occupancy_percent)}%` }}/>
        </div>
        <div className="mt-2 text-sm font-bold text-slate-700">{s.planned_manhours} van {s.available_manhours} manuren gepland</div>
      </div>

      <div className="mt-6 pb-card p-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Disclaimer</div>
        <div className="text-sm font-semibold text-slate-700">CAO-vergoedingen en bedragen worden berekend op basis van de instellingen van je bedrijf. Controleer altijd tegen de actuele CAO Bouw & Infra.</div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="pb-card p-4">
      <div className="flex items-center gap-2 text-slate-700 mb-2"><Icon size={18}/><span className="text-xs font-bold uppercase tracking-widest">{label}</span></div>
      <div className="pb-heading text-3xl font-black">{value}</div>
      {sub && <div className="text-xs font-semibold text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
